import { useCallback, useEffect, useMemo, useState } from 'react';
import { fetchSearchNotes } from '@/services/noteGoApi/noteGoApiComponents';
import { NoteOut } from '@/services/noteGoApi/noteGoApiSchemas';
import { useAuthStore } from '@/stores/authStore';
import { SECOND_BRAIN_NOTES_LIMIT } from '../config';
import { SecondBrainChat, SecondBrainContextNote } from '../types';
import { errorReporter } from '@/services/errorReportingService';
import { getQuestionKeywords, rankContextNotes } from '@/features/secondBrain/utils/searchNotes';

interface NotesSearchResponse {
  notes?: NoteOut[];
  total?: number;
}

const mapNotesResponse = (response: NotesSearchResponse | NoteOut[]): NoteOut[] => {
  if (Array.isArray(response)) {
    return response;
  }
  const notes = Array.isArray(response?.notes) ? response.notes : [];
  return notes;
};

export interface UseSecondBrainNotesReturn {
  contextNotesPreview: NoteOut[];
  displayedContextNotes: SecondBrainContextNote[];
  isLoadingPreview: boolean;
  isFetchingContextNotes: boolean;
  setIsFetchingContextNotes: (value: boolean) => void;
  setContextNotesPreview: (notes: NoteOut[]) => void;
  fetchContextNotes: (tags: string[], question?: string, signal?: AbortSignal) => Promise<NoteOut[]>;
  clearContextNotesPreview: () => void;
}

export const useSecondBrainNotes = (
  selectedTags: string[],
  activeChat: SecondBrainChat | null,
  isStreaming: boolean,
): UseSecondBrainNotesReturn => {
  const { userToken } = useAuthStore();

  const [contextNotesPreview, setContextNotesPreview] = useState<NoteOut[]>([]);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [isFetchingContextNotes, setIsFetchingContextNotes] = useState(false);

  const displayedContextNotes = useMemo(() => {
    if (activeChat?.contextNotes?.length) {
      return activeChat.contextNotes;
    }

    return contextNotesPreview.map((note) => ({
      id: note.id,
      title: note.title ?? null,
      summary: note.summary ?? null,
      createdAt: note.created_at ?? null,
      tags: note.tags ?? [],
      thumbnailUrl: note.thumbnail_list_url ?? note.thumbnail_url ?? null,
    }));
  }, [activeChat?.contextNotes, contextNotesPreview]);

  const fetchContextNotes = useCallback(
    async (tags: string[], question = '', signal?: AbortSignal): Promise<NoteOut[]> => {
      if (!userToken) {
        return [];
      }

      const keywords = tags.length === 0 ? getQuestionKeywords(question) : [];
      // No tags and nothing searchable would otherwise send the newest notes as context.
      if (tags.length === 0 && keywords.length === 0) {
        return [];
      }
      const response = await fetchSearchNotes(
        {
          headers: { authorization: `Bearer ${userToken}` },
          queryParams: {
            ...(tags.length > 0 ? { tags: tags.join(',') } : {}),
            ...(keywords.length > 0 ? { keywords: keywords.join(' ') } : {}),
            shared: false,
            limit: keywords.length > 0 ? 50 : SECOND_BRAIN_NOTES_LIMIT,
            sort_by: 'created_at',
            sort_order: 'desc',
          },
        },
        signal,
      );

      return rankContextNotes(mapNotesResponse(response), keywords);
    },
    [userToken],
  );

  const clearContextNotesPreview = useCallback(() => {
    setContextNotesPreview([]);
  }, []);

  // Fetch preview notes when tags change
  useEffect(() => {
    const controller = new AbortController();
    const fetchPreviewNotes = async () => {
      if (!userToken || selectedTags.length === 0 || isStreaming) {
        setContextNotesPreview([]);
        setIsLoadingPreview(false);
        return;
      }

      setIsLoadingPreview(true);
      try {
        const notes = await fetchContextNotes(selectedTags, '', controller.signal);
        if (controller.signal.aborted) return;
        setContextNotesPreview(notes);
      } catch (error) {
        if (controller.signal.aborted) return;
        errorReporter.expected('Failed to fetch Second Brain preview notes', error, {
          feature: 'secondBrain',
          operation: 'fetchPreviewNotes',
        });
        setContextNotesPreview([]);
      } finally {
        if (!controller.signal.aborted) setIsLoadingPreview(false);
      }
    };

    void fetchPreviewNotes();
    return () => controller.abort();
  }, [selectedTags, userToken, isStreaming, fetchContextNotes]);

  return {
    contextNotesPreview,
    displayedContextNotes,
    isLoadingPreview,
    isFetchingContextNotes,
    setIsFetchingContextNotes,
    setContextNotesPreview,
    fetchContextNotes,
    clearContextNotesPreview,
  };
};
